<template>
	<v-card :class="embedded ? '' : 'ma-5'" flat class="yealink-panel-main">
		<v-card-text :class="embedded ? 'pa-3' : 'pa-0'">
			<!-- Loading State -->
			<div v-if="loading" class="d-flex flex-column justify-center align-center py-8">
				<v-progress-circular indeterminate color="cyan" />
				<span class="mt-3 text--secondary">{{ $t('loading') }}...</span>
			</div>

			<!-- Error State -->
			<v-alert
				v-else-if="error && !noSiteFound"
				type="error"
				outlined
				dense
				class="mb-0"
			>
				{{ error }}
			</v-alert>

			<!-- No Site Found State -->
			<div v-else-if="noSiteFound" class="no-site-state">
				<v-icon size="64" color="grey lighten-1">mdi-domain-off</v-icon>
				<span class="no-site-text">{{ $t('yealink-no-site-found') }}</span>
				<v-btn
					color="primary"
					:loading="creatingSite"
					@click="createSite"
				>
					<v-icon left>mdi-plus</v-icon>
					{{ $t('yealink-create-site') }}
				</v-btn>
			</div>

			<!-- Content -->
			<div v-else-if="data" class="yealink-content">
				<!-- Site Header -->
				<div class="site-panel">
					<div class="site-panel-header">
						<div class="site-panel-left">
							<div class="site-panel-info">
								<div class="site-panel-name-row">
									<v-icon size="16" color="#5a6577" class="mr-1">mdi-domain</v-icon>
									<span class="site-panel-name">{{ data.site?.name || '-' }}</span>
									<span class="site-panel-id">#{{ data.site?.id || '-' }}</span>
									<v-btn
										icon
										x-small
										class="change-site-btn"
										@click="openSiteSelectionDialog"
									>
										<v-icon x-small color="#8a94a6">mdi-swap-horizontal</v-icon>
									</v-btn>
								</div>
								<div class="site-panel-metrics">
									<span class="metric-badge">
										<strong>{{ data.total || 0 }}</strong> {{ $t('yealink-devices') }}
									</span>
									<span class="metric-sep">·</span>
									<span class="metric-badge metric-online">
										<span class="metric-dot" />
										{{ onlineCount }} {{ $t('yealink-online') }}
									</span>
									<span class="metric-badge metric-offline">
										<span class="metric-dot" />
										{{ offlineCount }} {{ $t('yealink-offline') }}
									</span>
									<span class="metric-badge metric-pending">
										<span class="metric-dot" />
										{{ pendingCount }} {{ $t('yealink-pending') }}
									</span>
									<span class="metric-badge metric-registered">
										<span class="metric-dot" />
										{{ registeredCount }} {{ $t('yealink-registered') }}
									</span>
									<span class="metric-badge metric-unregistered">
										<span class="metric-dot" />
										{{ unregisteredCount }} {{ $t('yealink-unregistered') }}
									</span>
									<span class="metric-badge metric-dnd">
										<span class="metric-dot" />
										{{ dndCount }} {{ $t('yealink-status-dnd') }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Network Toolbox (transport / STUN, always fresh from YMCS) -->
					<yealink-network-toolbox
						ref="networkToolbox"
						:site-id="selectedSiteId?.id || null"
						@scan-update="onNetworkScanUpdate"
						@scanning="val => networkScanning = val"
						@rebooted="fetchDevices({ silent: true })"
						@reboot-device="onToolboxRebootDevice"
					/>

					<!-- Devices Toolbar -->
					<div class="devices-toolbar">
						<v-text-field
							v-model="searchQuery"
							dense
							hide-details
							outlined
							clearable
							:placeholder="$t('search')"
							class="search-field"
							prepend-inner-icon="mdi-magnify"
						/>
						<v-btn
							outlined
							class="expand-all-btn hidden-xs-only"
							@click="toggleAllDevices"
						>
							<v-icon small left>{{ hasAnyExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
							{{ hasAnyExpanded ? $t('yealink-collapse-all') : $t('yealink-expand-all') }}
						</v-btn>
						<v-btn icon small class="action-icon-btn expand-icon-btn hidden-sm-and-up" @click="toggleAllDevices">
							<v-icon small>{{ hasAnyExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
						</v-btn>
						<v-btn
							color="primary"
							class="add-device-btn hidden-xs-only"
							@click="openAddDeviceDialog"
						>
							<v-icon small left>mdi-plus</v-icon>
							{{ $t('yealink-add-device') }}
						</v-btn>
						<v-btn icon small class="action-icon-btn add-icon-btn hidden-sm-and-up" @click="openAddDeviceDialog">
							<v-icon small>mdi-plus</v-icon>
						</v-btn>
						<v-btn
							color="secondary"
							class="import-devices-btn hidden-xs-only"
							@click="openImportDevicesDialog"
						>
							<v-icon small left>mdi-file-upload</v-icon>
							{{ $t('yealink-import-devices') }}
						</v-btn>
						<v-btn icon small class="action-icon-btn import-icon-btn hidden-sm-and-up" @click="openImportDevicesDialog">
							<v-icon small>mdi-file-upload</v-icon>
						</v-btn>
					</div>
				</div>

				<!-- Devices List -->
				<div class="devices-section">
					<v-alert
						v-if="!filteredDevices.length"
						type="info"
						outlined
						dense
					>
						{{ $t('yealink-no-devices') }}
					</v-alert>

					<div v-else class="devices-list">
						<div
							v-for="device in filteredDevices"
							:key="device.id"
							class="device-card"
							:class="[`device-${getDeviceStatusClass(device)}`, { 'expanded': expandedDetails[device.id] }]"
						>
							<!-- Compact Header - Always Visible -->
							<div class="device-header" @click="toggleDetails(device.id)">
								<div class="device-status-indicator" :class="getDeviceStatusClass(device)" />
								<div class="device-title">
									<div class="device-name-row">
										<span class="device-name">{{ device.name || device.mac }}</span>
										<v-btn
											icon
											x-small
											class="edit-name-btn"
											@click.stop="openEditNameDialog(device)"
										>
											<v-icon x-small>mdi-pencil</v-icon>
										</v-btn>
										<v-chip
											x-small
											:color="getDeviceStatusChipColor(device)"
											dark
											class="status-chip ml-2"
										>
											{{ formatStatus(device.deviceStatus) }}
										</v-chip>
									</div>
									<span class="device-model">{{ device.modelName }}</span>
									<!-- Staleness Indicator -->
									<span
										v-if="device.lastReportTime"
										class="staleness-badge"
										:class="getStalenessLevel(device.lastReportTime)"
										:title="formatTimestamp(device.lastReportTime)"
									>
										<v-icon x-small :color="getStalenessColor(device.lastReportTime)">{{ getStalenessIcon(device.lastReportTime) }}</v-icon>
										<span class="staleness-text">{{ getStalenessText(device.lastReportTime) }}</span>
									</span>
								</div>

								<!-- Account Status Badges -->
								<div class="header-accounts">
									<template v-if="device.accounts && device.accounts.length">
										<v-chip
											v-for="(account, index) in device.accounts"
											:key="account.accountId || index"
											x-small
											:color="getAccountStatusColor(account.status)"
											dark
											class="account-chip"
											:title="getAccountStatusText(account.status)"
										>
											<v-icon x-small left>{{ getAccountStatusIcon(account.status) }}</v-icon>
											<span class="account-chip-text">{{ account.username || $t('yealink-line') + ' ' + account.lineId }}</span>
										</v-chip>
									</template>
									<v-chip
										v-else
										x-small
										color="error"
										dark
										class="account-chip"
									>
										<v-icon x-small left>mdi-account-off</v-icon>
										<span class="account-chip-text">{{ $t('yealink-no-accounts-short') }}</span>
									</v-chip>
								</div>

								<!-- Transport / STUN (live) -->
								<div class="device-net" :title="networkTooltip(device)">
									<template v-if="networkScanning || !networkByDevice[device.id]">
										<span class="net-chip net-chip-loading">
											<v-progress-circular indeterminate size="9" width="2" color="#00acc1" />
											<span>{{ $t('yealink-net-device-chip-scanning') }}</span>
										</span>
									</template>
									<template v-else-if="networkByDevice[device.id].state === 'ok'">
										<span class="net-chip net-chip-transport" :style="{ '--chip-color': networkTransportColor(networkByDevice[device.id].transport) }">
											<v-icon x-small>{{ networkTransportIcon(networkByDevice[device.id].transport) }}</v-icon>
											{{ networkTransportLabel(networkByDevice[device.id].transport) }}
										</span>
										<span class="net-chip net-chip-stun" :class="networkStunClass(networkByDevice[device.id])">
											<v-icon x-small>{{ networkByDevice[device.id].stun === true ? 'mdi-shield-check' : (networkByDevice[device.id].stun === 'mixed' ? 'mdi-shield-alert' : 'mdi-shield-off-outline') }}</v-icon>
											{{ networkStunLabel(networkByDevice[device.id]) }}
											<v-icon v-if="networkByDevice[device.id].stunMisconfigured" x-small color="#f59e0b" class="ml-1">mdi-alert</v-icon>
										</span>
									</template>
									<span v-else class="net-chip net-chip-unknown" :class="`state-${networkByDevice[device.id].state}`">
										<v-icon x-small>{{ networkByDevice[device.id].state === 'offline' ? 'mdi-power-plug-off' : 'mdi-alert-circle-outline' }}</v-icon>
										{{ networkByDevice[device.id].state === 'offline' ? $t('yealink-net-offline') : $t('yealink-net-error-short') }}
									</span>
								</div>

								<!-- Device Action Icons -->
								<div class="device-actions">
									<v-tooltip bottom>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												x-small
												v-bind="attrs"
												class="action-icon action-configure"
												v-on="on"
												@click.stop="$emit('configure-device', device.mac)"
											>
												<v-icon x-small>mdi-file-cog</v-icon>
											</v-btn>
										</template>
										<span>{{ $t('yealink-configure-device') }}</span>
									</v-tooltip>
									<v-tooltip bottom>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												x-small
												v-bind="attrs"
												class="action-icon action-reboot"
												v-on="on"
												@click.stop="openRebootDialog(device)"
											>
												<v-icon x-small>mdi-restart</v-icon>
											</v-btn>
										</template>
										<span>{{ $t('yealink-reboot') }}</span>
									</v-tooltip>
									<v-tooltip bottom>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												x-small
												v-bind="attrs"
												class="action-icon action-reset"
												v-on="on"
												@click.stop="openFactoryResetDialog(device)"
											>
												<v-icon x-small>mdi-backup-restore</v-icon>
											</v-btn>
										</template>
										<span>{{ $t('yealink-factory-reset') }}</span>
									</v-tooltip>
									<span class="action-separator" />
									<v-tooltip bottom>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												x-small
												v-bind="attrs"
												class="action-icon action-capture"
												v-on="on"
												@click.stop="openPacketCaptureDialog(device)"
											>
												<v-icon x-small>mdi-network-outline</v-icon>
											</v-btn>
										</template>
										<span>{{ $t('yealink-packet-capture') }}</span>
									</v-tooltip>
									<v-tooltip bottom>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												x-small
												v-bind="attrs"
												class="action-icon action-export"
												v-on="on"
												@click.stop="exportDeviceConfig(device)"
											>
												<v-icon x-small>mdi-file-export-outline</v-icon>
											</v-btn>
										</template>
										<span>{{ $t('yealink-export-config') }}</span>
									</v-tooltip>
									<v-tooltip bottom>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												x-small
												v-bind="attrs"
												class="action-icon action-copy"
												v-on="on"
												@click.stop="openCopyConfigDialog(device)"
											>
												<v-icon x-small>mdi-content-copy</v-icon>
											</v-btn>
										</template>
										<span>{{ $t('yealink-copy-config') }}</span>
									</v-tooltip>
								</div>

								<!-- Expand/Collapse Button -->
								<v-btn icon small class="expand-btn">
									<v-icon>{{ expandedDetails[device.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
								</v-btn>
							</div>

							<!-- Expanded Details -->
							<div v-if="expandedDetails[device.id]" class="device-body">
								<!-- Device Info Grid -->
								<div class="info-grid">
									<div class="info-item">
										<v-icon x-small class="mr-1">mdi-ethernet</v-icon>
										<span class="info-label">MAC:</span>
										<span class="info-value monospace">{{ formatMac(device.mac) }}</span>
										<v-btn icon x-small class="copy-btn" @click="copyToClipboard(device.mac)">
											<v-icon x-small>mdi-content-copy</v-icon>
										</v-btn>
									</div>
									<div class="info-item">
										<v-icon x-small class="mr-1">mdi-barcode</v-icon>
										<span class="info-label">{{ $t('yealink-serial') }}:</span>
										<span class="info-value monospace">{{ device.sn }}</span>
										<v-btn icon x-small class="copy-btn" @click="copyToClipboard(device.sn)">
											<v-icon x-small>mdi-content-copy</v-icon>
										</v-btn>
									</div>
									<div v-if="device.lanIp" class="info-item">
										<v-icon x-small class="mr-1">mdi-ip-network</v-icon>
										<span class="info-label">LAN IP:</span>
										<a
											:href="`https://${device.lanIp}`"
											target="_blank"
											class="info-value monospace ip-link"
											@click.stop
										>{{ device.lanIp }}</a>
										<v-btn icon x-small class="copy-btn" @click.stop="copyToClipboard(device.lanIp)">
											<v-icon x-small>mdi-content-copy</v-icon>
										</v-btn>
									</div>
									<div v-if="device.wanIp" class="info-item">
										<v-icon x-small class="mr-1">mdi-wan</v-icon>
										<span class="info-label">WAN IP:</span>
										<span class="info-value monospace">{{ device.wanIp }}</span>
										<v-btn icon x-small class="copy-btn" @click.stop="copyToClipboard(device.wanIp)">
											<v-icon x-small>mdi-content-copy</v-icon>
										</v-btn>
									</div>
									<div class="info-item">
										<v-icon x-small class="mr-1">mdi-cellphone-arrow-down</v-icon>
										<span class="info-label">{{ $t('yealink-firmware') }}:</span>
										<span class="info-value">{{ device.programVersion || '-' }}</span>
									</div>
									<div v-if="device.lastReportTime" class="info-item">
										<v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
										<span class="info-label">{{ $t('yealink-last-report') }}:</span>
										<span class="info-value">{{ formatTimestamp(device.lastReportTime) }}</span>
									</div>
								</div>

								<!-- Network Section (live config) -->
								<div v-if="networkByDevice[device.id] && networkByDevice[device.id].state === 'ok' && !networkScanning" class="network-section">
									<div class="accounts-header">
										<v-icon x-small class="mr-1">mdi-lan</v-icon>
										<span class="accounts-title">{{ $t('yealink-net-title') }}</span>
										<span class="network-scanned-at">{{ formatTimestamp(networkByDevice[device.id].scannedAt) }}</span>
									</div>
									<div class="network-grid">
										<div
											v-for="acc in networkByDevice[device.id].accounts"
											:key="acc.index"
											class="network-account"
										>
											<span class="line-number">{{ $t('yealink-net-account', { index: acc.index }) }}<template v-if="acc.username"> · <span class="monospace">{{ acc.username }}</span></template></span>
											<span class="net-chip net-chip-transport" :style="{ '--chip-color': networkTransportColor(acc.transport) }">{{ networkTransportLabel(acc.transport) }}</span>
											<span class="detail-label">{{ $t('yealink-net-nat') }}:</span>
											<span class="detail-value">{{ networkNatLabel(acc.natTraversal) }}</span>
											<span class="detail-label">{{ $t('yealink-net-port') }}:</span>
											<span class="detail-value monospace">{{ acc.port || '5060' }}</span>
										</div>
										<div class="network-account network-global">
											<span class="line-number">{{ $t('yealink-net-global-stun') }}</span>
											<span class="net-chip net-chip-stun" :class="networkByDevice[device.id].stunGlobalEnabled === null ? 'stun-default' : (networkByDevice[device.id].stunGlobalEnabled ? 'stun-on' : 'stun-off')">
												{{ networkByDevice[device.id].stunGlobalEnabled === null ? $t('yealink-net-stun-not-exported') : (networkByDevice[device.id].stunGlobalEnabled ? $t('yealink-net-stun-on') : $t('yealink-net-stun-off')) }}
											</span>
											<span class="detail-value monospace">{{ networkByDevice[device.id].stunServer || '—' }}</span>
											<span v-if="networkByDevice[device.id].stunMisconfigured" class="network-warn">
												<v-icon x-small color="#f59e0b" class="mr-1">mdi-alert</v-icon>{{ $t('yealink-net-misconfigured') }}
											</span>
										</div>
									</div>
								</div>

								<!-- Accounts Section -->
								<div class="accounts-section">
									<!-- No Accounts Error -->
									<v-alert
										v-if="!device.accounts || !device.accounts.length"
										type="error"
										outlined
										dense
										class="mb-0"
									>
										<v-icon small left>mdi-account-off</v-icon>
										{{ $t('yealink-no-accounts') }}
									</v-alert>

									<!-- Accounts List (always expanded) -->
									<template v-else>
										<div class="accounts-header">
											<v-icon x-small class="mr-1">mdi-account-multiple</v-icon>
											<span class="accounts-title">{{ $t('yealink-accounts') }}</span>
											<v-chip x-small class="ml-2" color="grey lighten-2">
												{{ device.accounts.length }}
											</v-chip>
										</div>

										<div class="accounts-list">
											<div
												v-for="(account, index) in device.accounts"
												:key="account.accountId || index"
												class="account-item"
											>
												<div class="account-line">
													<span class="line-number">{{ $t('yealink-line') }} {{ account.lineId }}</span>
													<v-chip
														x-small
														:color="getAccountStatusColor(account.status)"
														dark
													>
														<v-icon x-small left>{{ getAccountStatusIcon(account.status) }}</v-icon>
														{{ getAccountStatusText(account.status) }}
													</v-chip>
												</div>
												<div class="account-details">
													<div class="account-detail">
														<span class="detail-label">{{ $t('yealink-username') }}:</span>
														<span class="detail-value monospace">{{ account.username || '-' }}</span>
													</div>
													<div v-if="account.registerName" class="account-detail">
														<span class="detail-label">{{ $t('yealink-register-name') }}:</span>
														<span class="detail-value monospace">{{ account.registerName }}</span>
													</div>
													<div v-if="account.accountServer" class="account-detail">
														<span class="detail-label">{{ $t('yealink-server') }}:</span>
														<span class="detail-value monospace">{{ account.accountServer }}</span>
													</div>
												</div>
											</div>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Empty State -->
			<div v-else class="empty-state">
				<v-icon size="64" color="grey lighten-1">mdi-phone-voip</v-icon>
				<span class="empty-text">{{ $t('yealink-no-data') }}</span>
				<v-btn color="primary" outlined small @click="fetchDevices">
					<v-icon small left>mdi-refresh</v-icon>
					{{ $t('refresh') }}
				</v-btn>
			</div>
		</v-card-text>

		<!-- Reboot Confirmation Dialog -->
		<v-dialog v-model="rebootDialog.open" max-width="400" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="warning white--text">
					<v-icon color="white" class="mr-2">mdi-restart</v-icon>
					{{ $t('yealink-reboot') }}
					<v-spacer />
					<v-btn icon small dark @click="rebootDialog.open = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<p>{{ $t('yealink-reboot-confirm') }}</p>
					<p class="font-weight-bold mb-0">
						{{ rebootDialog.device?.name || rebootDialog.device?.mac }}
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="rebootDialog.open = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="warning"
						:loading="rebootDialog.loading"
						@click="rebootDevice"
					>
						{{ $t('yealink-reboot') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Factory Reset Confirmation Dialog -->
		<v-dialog v-model="factoryResetDialog.open" max-width="450" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="error white--text">
					<v-icon color="white" class="mr-2">mdi-backup-restore</v-icon>
					{{ $t('yealink-factory-reset') }}
					<v-spacer />
					<v-btn icon small dark @click="closeFactoryResetDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<v-alert type="error" outlined dense class="mb-3">
						{{ $t('yealink-factory-reset-warning') }}
					</v-alert>
					<p>{{ $t('yealink-factory-reset-confirm') }}</p>
					<p class="font-weight-bold">
						{{ factoryResetDialog.device?.name || factoryResetDialog.device?.mac }}
					</p>
					<v-text-field
						v-model="factoryResetDialog.confirmation"
						:label="$t('yealink-type-reset-confirm')"
						outlined
						dense
						class="mt-2"
					/>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closeFactoryResetDialog">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="error"
						:loading="factoryResetDialog.loading"
						:disabled="factoryResetDialog.confirmation !== 'RESET'"
						@click="factoryResetDevice"
					>
						{{ $t('yealink-factory-reset') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Packet Capture Dialog -->
		<v-dialog v-model="packetCaptureDialog.open" max-width="500" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="purple white--text">
					<v-icon color="white" class="mr-2">mdi-network-outline</v-icon>
					{{ $t('yealink-packet-capture') }}
					<v-spacer />
					<v-btn icon small dark @click="closePacketCaptureDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<p class="mb-3">
						{{ $t('yealink-packet-capture-description') }}
					</p>
					<p class="font-weight-bold mb-3">
						{{ packetCaptureDialog.device?.name || packetCaptureDialog.device?.mac }}
					</p>

					<!-- Capture Status -->
					<div v-if="packetCaptureDialog.status" class="capture-status mb-3">
						<v-alert
							:type="packetCaptureDialog.status === 'success' ? 'success' : packetCaptureDialog.status === 'failure' ? 'error' : 'info'"
							outlined
							dense
						>
							<div class="d-flex align-center">
								<v-progress-circular
									v-if="packetCaptureDialog.status === 'inprogress'"
									indeterminate
									size="16"
									width="2"
									class="mr-2"
								/>
								{{ getPacketCaptureStatusText() }}
							</div>
							<div v-if="packetCaptureDialog.url" class="mt-2">
								<v-btn small color="primary" :href="packetCaptureDialog.url" target="_blank">
									<v-icon small left>mdi-download</v-icon>
									{{ $t('yealink-download-capture') }}
								</v-btn>
							</div>
						</v-alert>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closePacketCaptureDialog">
						{{ $t('close') }}
					</v-btn>
					<v-btn
						v-if="!packetCaptureDialog.status || packetCaptureDialog.status === 'success' || packetCaptureDialog.status === 'failure'"
						color="purple"
						dark
						:loading="packetCaptureDialog.loading"
						@click="startPacketCapture"
					>
						<v-icon left small>mdi-play</v-icon>
						{{ $t('yealink-start-capture') }}
					</v-btn>
					<v-btn
						v-else-if="packetCaptureDialog.status === 'inprogress'"
						color="error"
						:loading="packetCaptureDialog.loading"
						@click="stopPacketCapture"
					>
						<v-icon left small>mdi-stop</v-icon>
						{{ $t('yealink-stop-capture') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Copy Config Dialog -->
		<v-dialog v-model="copyConfigDialog.open" max-width="500" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="teal white--text">
					<v-icon color="white" class="mr-2">mdi-content-copy</v-icon>
					{{ $t('yealink-copy-config') }}
					<v-spacer />
					<v-btn icon small dark @click="closeCopyConfigDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<p class="mb-3">
						{{ $t('yealink-copy-config-description') }}
					</p>
					<p class="mb-2">
						<strong>{{ $t('yealink-target-device') }}:</strong>
					</p>
					<p class="font-weight-bold mb-3 ml-2">
						{{ copyConfigDialog.device?.name || copyConfigDialog.device?.mac }}
					</p>

					<v-autocomplete
						v-model="copyConfigDialog.sourceDevice"
						:items="availableSourceDevices"
						:label="$t('yealink-source-device')"
						item-text="displayName"
						item-value="id"
						return-object
						outlined
						dense
						:disabled="copyConfigDialog.loading"
					>
						<template #item="{ item }">
							<v-list-item-content>
								<v-list-item-title>{{ item.name || item.mac }}</v-list-item-title>
								<v-list-item-subtitle>{{ item.modelName }} - {{ formatMac(item.mac) }}</v-list-item-subtitle>
							</v-list-item-content>
						</template>
					</v-autocomplete>

					<v-alert
						v-if="copyConfigDialog.error"
						type="error"
						outlined
						dense
						class="mt-2"
					>
						{{ copyConfigDialog.error }}
					</v-alert>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closeCopyConfigDialog">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="teal"
						dark
						:loading="copyConfigDialog.loading"
						:disabled="!copyConfigDialog.sourceDevice"
						@click="copyConfig"
					>
						<v-icon left small>mdi-content-copy</v-icon>
						{{ $t('yealink-copy-config') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Export Config Progress Dialog -->
		<v-dialog v-model="exportConfigDialog.open" max-width="450" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="blue white--text">
					<v-icon color="white" class="mr-2">mdi-file-export-outline</v-icon>
					{{ $t('yealink-export-config') }}
					<v-spacer />
					<v-btn icon small dark @click="closeExportConfigDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<p class="mb-3">
						{{ exportConfigDialog.device?.name || exportConfigDialog.device?.mac }}
					</p>

					<v-alert
						:type="exportConfigDialog.status === 'success' ? 'success' : exportConfigDialog.status === 'failure' ? 'error' : 'info'"
						outlined
						dense
					>
						<div class="d-flex align-center">
							<v-progress-circular
								v-if="exportConfigDialog.status === 'inprogress'"
								indeterminate
								size="16"
								width="2"
								class="mr-2"
							/>
							{{ getExportStatusText() }}
						</div>
						<div v-if="exportConfigDialog.url" class="mt-2">
							<v-btn small color="primary" :href="exportConfigDialog.url" target="_blank">
								<v-icon small left>mdi-download</v-icon>
								{{ $t('yealink-download-config') }}
							</v-btn>
						</div>
					</v-alert>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closeExportConfigDialog">
						{{ $t('close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Add Device Dialog -->
		<v-dialog
			v-model="addDeviceDialog.open"
			max-width="500"
			persistent
			:fullscreen="$vuetify.breakpoint.xsOnly"
			content-class="devops-subdialog"
		>
			<v-card>
				<v-card-title class="primary white--text">
					<v-icon color="white" class="mr-2">mdi-plus</v-icon>
					{{ $t('yealink-add-device') }}
					<v-spacer />
					<v-btn icon small dark @click="closeAddDeviceDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<v-form ref="addDeviceForm" v-model="addDeviceDialog.valid">
						<!-- Scan Label Photo Button -->
						<div class="scan-label-wrapper mb-3">
							<input
								ref="labelPhotoInput"
								type="file"
								accept="image/*,.heic,.heif"
								class="d-none"
								@change="onLabelPhotoSelected"
							>
							<v-btn
								class="scan-label-btn"
								outlined
								block
								:loading="addDeviceDialog.scanLoading"
								:disabled="addDeviceDialog.loading"
								@click="$refs.labelPhotoInput.click()"
							>
								<v-icon>mdi-camera</v-icon>
								{{ $t('yealink-scan-device-label') }}
							</v-btn>
							<p class="text-caption grey--text mt-1 mb-0 text-center">
								{{ $t('yealink-scan-label-hint') }}
							</p>
							<v-alert
								v-if="addDeviceDialog.scanError"
								type="warning"
								outlined
								dense
								dismissible
								class="mt-2"
							>
								{{ addDeviceDialog.scanError }}
							</v-alert>
						</div>
						<v-text-field
							v-model="addDeviceDialog.mac"
							:label="$t('yealink-mac-address') + ' *'"
							:rules="[v => !!v || $t('required')]"
							outlined
							dense
							:disabled="addDeviceDialog.loading"
							placeholder="001565XXXXXX"
							class="mb-2 copy-field"
							@input="addDeviceDialog.mac = sanitizeDeviceInput(addDeviceDialog.mac)"
						>
							<template v-if="addDeviceDialog.mac" #append-outer>
								<v-icon small class="copy-icon" @click="copyToClipboard(addDeviceDialog.mac)">mdi-content-copy</v-icon>
							</template>
						</v-text-field>
						<v-text-field
							v-model="addDeviceDialog.sn"
							:label="$t('yealink-serial-number') + ' *'"
							:rules="[v => !!v || $t('required')]"
							outlined
							dense
							:disabled="addDeviceDialog.loading"
							class="mb-2 copy-field"
							@input="addDeviceDialog.sn = sanitizeDeviceInput(addDeviceDialog.sn)"
						>
							<template v-if="addDeviceDialog.sn" #append-outer>
								<v-icon small class="copy-icon" @click="copyToClipboard(addDeviceDialog.sn)">mdi-content-copy</v-icon>
							</template>
						</v-text-field>
						<v-autocomplete
							v-model="addDeviceDialog.modelId"
							:items="deviceModels"
							item-text="name"
							item-value="id"
							:label="$t('yealink-device-model') + ' *'"
							:rules="[v => !!v || $t('required')]"
							outlined
							dense
							clearable
							:disabled="addDeviceDialog.loading"
							:loading="deviceModelsLoading"
							:filter="(item, queryText) => item.name.toLowerCase().includes(queryText.toLowerCase())"
							class="mb-2"
						/>
						<v-text-field
							v-model="addDeviceDialog.name"
							:label="$t('yealink-device-name')"
							outlined
							dense
							:disabled="addDeviceDialog.loading"
							:hint="$t('optional')"
							persistent-hint
						/>
						<v-checkbox
							v-model="addDeviceDialog.skipRps"
							:label="$t('yealink-skip-rps')"
							:disabled="addDeviceDialog.loading"
							hide-details
							class="mt-0"
						/>
						<div class="rps-hint mt-3">
							<v-icon small color="info" class="mr-1">mdi-information-outline</v-icon>
							<span>{{ $t('yealink-rps-hint-prefix') }} <a href="https://us.ymcs.yealink.com/manager/rps-equipment?platform=RPS" target="_blank" rel="noopener noreferrer" class="rps-link">YMCS RPS</a></span>
						</div>
					</v-form>

					<v-alert
						v-if="addDeviceDialog.error"
						type="error"
						outlined
						dense
						class="mt-3 add-device-error"
					>
						{{ addDeviceDialog.error }}
					</v-alert>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closeAddDeviceDialog">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="primary"
						:loading="addDeviceDialog.loading"
						:disabled="!addDeviceDialog.valid"
						@click="addDevice"
					>
						<v-icon left small>mdi-plus</v-icon>
						{{ $t('yealink-add-device') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Import Devices Dialog -->
		<v-dialog
			v-model="importDevicesDialog.open"
			max-width="600"
			persistent
			:fullscreen="$vuetify.breakpoint.xsOnly"
			content-class="devops-subdialog"
		>
			<v-card>
				<v-card-title class="primary white--text">
					<v-icon color="white" class="mr-2">mdi-file-upload</v-icon>
					{{ $t('yealink-import-devices') }}
					<v-spacer />
					<v-btn icon small dark @click="closeImportDevicesDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<v-alert type="info" outlined dense class="mb-3">
						{{ $t('yealink-import-file-hint') }}
					</v-alert>

					<v-file-input
						v-model="importDevicesDialog.file"
						:label="$t('yealink-import-select-file')"
						accept=".xlsx,.xls"
						outlined
						dense
						prepend-icon="mdi-microsoft-excel"
						:disabled="importDevicesDialog.loading"
						show-size
					/>

					<!-- Results -->
					<div v-if="importDevicesDialog.results" class="mt-4">
						<v-alert
							v-if="importDevicesDialog.results.added > 0"
							type="success"
							outlined
							dense
						>
							{{ $t('yealink-import-success', { count: importDevicesDialog.results.added, total: importDevicesDialog.results.total }) }}
						</v-alert>

						<v-alert
							v-if="importDevicesDialog.results.failed > 0"
							type="warning"
							outlined
							dense
						>
							{{ $t('yealink-import-failures', { count: importDevicesDialog.results.failed }) }}
						</v-alert>

						<v-simple-table
							v-if="importDevicesDialog.results.results.failed.length"
							dense
							class="mt-2"
						>
							<thead>
								<tr>
									<th>SKU</th>
									<th>MAC</th>
									<th>{{ $t('yealink-serial-number') }}</th>
									<th>{{ $t('yealink-import-error') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(item, i) in importDevicesDialog.results.results.failed" :key="i">
									<td>{{ item.sku }}</td>
									<td>{{ item.mac }}</td>
									<td>{{ item.sn }}</td>
									<td class="error--text">
										{{ item.error }}
									</td>
								</tr>
							</tbody>
						</v-simple-table>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closeImportDevicesDialog">
						{{ importDevicesDialog.results ? $t('close') : $t('cancel') }}
					</v-btn>
					<v-btn
						v-if="!importDevicesDialog.results"
						color="primary"
						:loading="importDevicesDialog.loading"
						:disabled="!importDevicesDialog.file"
						@click="importDevices"
					>
						<v-icon left small>mdi-upload</v-icon>
						{{ $t('yealink-import-devices') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Edit Device Name Dialog -->
		<v-dialog v-model="editNameDialog.open" max-width="400" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="primary white--text">
					<v-icon color="white" class="mr-2">mdi-pencil</v-icon>
					{{ $t('yealink-edit-name') }}
					<v-spacer />
					<v-btn icon small dark @click="closeEditNameDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<v-text-field
						v-model="editNameDialog.name"
						:label="$t('yealink-device-name')"
						outlined
						dense
						:disabled="editNameDialog.loading"
						autofocus
						@keyup.enter="saveDeviceName"
					/>
					<v-alert
						v-if="editNameDialog.error"
						type="error"
						outlined
						dense
						class="mt-2 mb-0 error-alert"
					>
						{{ editNameDialog.error }}
					</v-alert>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closeEditNameDialog">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="primary"
						:loading="editNameDialog.loading"
						@click="saveDeviceName"
					>
						<v-icon left small>mdi-content-save</v-icon>
						{{ $t('save') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Site Selection Dialog -->
		<v-dialog v-model="siteSelectionDialog.open" max-width="500" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="primary white--text">
					<v-icon color="white" class="mr-2">mdi-domain</v-icon>
					{{ $t('yealink-change-site') }}
					<v-spacer />
					<v-btn icon small dark @click="closeSiteSelectionDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<v-autocomplete
						ref="siteAutocomplete"
						v-model="siteSelectionDialog.selectedSite"
						:items="sites"
						:loading="sitesLoading"
						:search-input.sync="siteSearchQuery"
						:filter="siteFilter"
						item-text="name"
						item-value="id"
						:label="$t('yealink-select-site')"
						:placeholder="$t('yealink-search-site-placeholder')"
						outlined
						hide-details
						clearable
						return-object
						prepend-inner-icon="mdi-magnify"
						auto-select-first
						@change="onSiteAutocompleteChange"
					>
						<template #item="{ item }">
							<v-list-item-content>
								<v-list-item-title>{{ item.name }}</v-list-item-title>
								<v-list-item-subtitle class="text-caption">
									ID: {{ item.id }}
								</v-list-item-subtitle>
							</v-list-item-content>
						</template>
						<template #selection="{ item }">
							<span class="text-truncate site-selection-text">{{ item.name }}</span>
						</template>
					</v-autocomplete>
					<p class="text-caption grey--text mt-2 mb-0">
						{{ $t('yealink-site-selection-hint') }}
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="closeSiteSelectionDialog">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn
						color="primary"
						@click="confirmSiteSelection"
					>
						{{ $t('confirm') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Hidden container for html5-qrcode scanFileV2 -->
		<div id="label-scanner-tmp" />
	</v-card>
</template>

<script>
import moment from "moment";

import YealinkNetworkToolbox from "@/components/support/YealinkNetworkToolbox.vue";

const NET_TRANSPORT_COLORS = { 0: "#f59e0b", 1: "#2563eb", 2: "#16a34a", 3: "#7c3aed" };

export default {
	name: "YealinkPanel",

	components: {
		YealinkNetworkToolbox
	},

	props: {
		embedded: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			loading: false,
			error: null,
			noSiteFound: false,
			creatingSite: false,
			data: null,
			searchQuery: "",
			expandedDetails: {},
			networkScan: null,
			networkScanning: false,
			// Site selector state
			sites: [],
			sitesLoading: false,
			selectedSiteId: null,
			siteSearchQuery: "",
			siteSelectionDialog: {
				open: false,
				selectedSite: null
			},
			// Dialog states
			rebootDialog: {
				open: false,
				loading: false,
				device: null
			},
			factoryResetDialog: {
				open: false,
				loading: false,
				device: null,
				confirmation: ""
			},
			packetCaptureDialog: {
				open: false,
				loading: false,
				device: null,
				diagnosisId: null,
				status: null,
				url: null,
				pollInterval: null
			},
			copyConfigDialog: {
				open: false,
				loading: false,
				device: null,
				sourceDevice: null,
				error: null
			},
			exportConfigDialog: {
				open: false,
				loading: false,
				device: null,
				diagnosisId: null,
				status: null,
				url: null,
				pollInterval: null
			},
			addDeviceDialog: {
				open: false,
				loading: false,
				valid: false,
				mac: "",
				sn: "",
				modelId: null,
				name: "",
				skipRps: false,
				error: null,
				scanLoading: false,
				scanError: null
			},
			deviceModels: [],
			deviceModelsLoading: false,
			importDevicesDialog: {
				open: false,
				loading: false,
				file: null,
				results: null,
				error: null
			},
			editNameDialog: {
				open: false,
				loading: false,
				device: null,
				name: "",
				error: null
			}
		};
	},

	computed: {
		/**
		 * Normalize a string for accent-insensitive search
		 * Removes diacritics (accents) and converts to lowercase
		 */
		normalizeString() {
			return (str) => {
				if (!str) return "";
				return str
					.normalize("NFD")
					.replace(/[\u0300-\u036f]/g, "")
					.toLowerCase();
			};
		},

		/**
		 * Filter sites based on the search query with normalized (accent-insensitive) search
		 */
		filteredSites() {
			if (!this.sites?.length) return [];

			if (!this.siteSearchQuery) {
				return this.sites;
			}

			const normalizedQuery = this.normalizeString(this.siteSearchQuery);

			return this.sites.filter(site => {
				const normalizedName = this.normalizeString(site.name);
				const normalizedId = this.normalizeString(site.id);

				return normalizedName.includes(normalizedQuery) ||
					normalizedId.includes(normalizedQuery);
			});
		},

		hasAnyExpanded() {
			if (!this.data?.devices) return false;
			return this.data.devices.some(d => this.expandedDetails[d.id]);
		},

		networkByDevice() {
			const map = {};

			for (const d of this.networkScan?.devices || []) {
				map[d.deviceId] = d;
			}
			return map;
		},

		onlineCount() {
			if (!this.data?.devices) return 0;
			return this.data.devices.filter(d => d.deviceStatus?.toLowerCase() === "online").length;
		},

		offlineCount() {
			if (!this.data?.devices) return 0;
			return this.data.devices.filter(d => d.deviceStatus?.toLowerCase() === "offline").length;
		},

		pendingCount() {
			if (!this.data?.devices) return 0;
			return this.data.devices.filter(d => d.deviceStatus?.toLowerCase() === "pending").length;
		},

		registeredCount() {
			if (!this.data?.devices) return 0;
			return this.data.devices.filter(d =>
				d.accounts?.some(a => a.status === 1 || a.status === 2)
			).length;
		},

		unregisteredCount() {
			if (!this.data?.devices) return 0;
			return this.data.devices.filter(d =>
				d.accounts?.some(a => a.status === 3 || a.status === 4)
			).length;
		},

		dndCount() {
			if (!this.data?.devices) return 0;
			return this.data.devices.filter(d =>
				d.accounts?.some(a => a.status === 2)
			).length;
		},

		filteredDevices() {
			if (!this.data?.devices) return [];

			let devices = [...this.data.devices];

			// Filter by search query if present
			if (this.searchQuery) {
				const query = this.searchQuery.toLowerCase();

				devices = devices.filter(device => {
					const searchFields = [
						device.name,
						device.mac,
						device.sn,
						device.modelName,
						device.lanIp,
						device.programVersion,
						...device.accounts?.map(a => a.username) || []
					].filter(Boolean);

					return searchFields.some(field =>
						field.toLowerCase().includes(query)
					);
				});
			}

			// Sort: offline/pending first, then online. Within each group, sort by first account name
			devices.sort((a, b) => {
				const aOnline = this.isDeviceOnline(a);
				const bOnline = this.isDeviceOnline(b);

				// Different online status: offline/pending comes first
				if (aOnline !== bOnline) {
					return aOnline ? 1 : -1;
				}

				// Same online status: sort by first account name
				const aName = this.getFirstAccountName(a).toLowerCase();
				const bName = this.getFirstAccountName(b).toLowerCase();

				return aName.localeCompare(bName, undefined, { numeric: true });
			});

			return devices;
		},

		availableSourceDevices() {
			if (!this.data?.devices || !this.copyConfigDialog.device) return [];
			return this.data.devices
				.filter(d => d.id !== this.copyConfigDialog.device.id)
				.map(d => ({
					...d,
					displayName: `${d.name || d.mac} (${d.modelName})`
				}));
		}
	},

	watch: {
		loading(val) {
			this.$emit("update:loading", val);
		}
	},

	mounted() {
		this.fetchSites();
		this.fetchDevices();
		this.fetchDeviceModels();
	},

	beforeDestroy() {
		if (this.packetCaptureDialog.pollInterval) {
			clearInterval(this.packetCaptureDialog.pollInterval);
		}
		if (this.exportConfigDialog.pollInterval) {
			clearInterval(this.exportConfigDialog.pollInterval);
		}
	},

	methods: {
		onNetworkScanUpdate(scan) {
			this.networkScan = scan;
		},

		onToolboxRebootDevice(deviceId) {
			const device = this.data?.devices?.find(d => d.id === deviceId);

			if (device) this.openRebootDialog(device);
		},

		networkTransportLabel(value) {
			if (value === "mixed") return this.$t("yealink-net-mixed");
			if (value === null || value === undefined) return "—";
			return { 0: "UDP", 1: "TCP", 2: "TLS", 3: "DNS-NAPTR" }[value] || `#${value}`;
		},

		networkTransportColor(value) {
			if (value === "mixed") return "#dc2626";
			return NET_TRANSPORT_COLORS[value] || "#9ca3af";
		},

		networkTransportIcon(value) {
			if (value === "mixed") return "mdi-call-split";
			return { 0: "mdi-lightning-bolt", 1: "mdi-swap-horizontal", 2: "mdi-lock" }[value] || "mdi-help-circle-outline";
		},

		networkStunLabel(net) {
			if (net.stun === "mixed") return `${this.$t("yealink-net-stun")} ${this.$t("yealink-net-mixed")}`;
			return net.stun ? this.$t("yealink-net-stun") : this.$t("yealink-net-no-stun");
		},

		networkStunClass(net) {
			if (net.stun === "mixed") return "stun-mixed";
			return net.stun ? "stun-on" : "stun-off";
		},

		networkNatLabel(value) {
			switch (Number(value)) {
				case 1: return this.$t("yealink-net-nat-stun");
				case 2: return this.$t("yealink-net-nat-ice");
				default: return this.$t("yealink-net-nat-disabled");
			}
		},

		networkTooltip(device) {
			const net = this.networkByDevice[device.id];

			if (this.networkScanning || !net) return this.$t("yealink-net-device-chip-scanning");
			if (net.state === "offline") return this.$t("yealink-net-device-chip-offline");
			if (net.state !== "ok") return `${this.$t("yealink-net-device-chip-error")}${net.error ? `: ${net.error}` : ""}`;
			const parts = [
				`${this.$t("yealink-net-transport")}: ${this.networkTransportLabel(net.transport)}`,
				`${this.$t("yealink-net-stun")}: ${net.stun === "mixed" ? this.$t("yealink-net-mixed") : (net.stun ? this.$t("yealink-net-stun-on") : this.$t("yealink-net-stun-off"))}`
			];

			if (net.stunServer) parts.push(net.stunServer);
			if (net.stunMisconfigured) parts.push(this.$t("yealink-net-misconfigured"));
			return parts.join("\n");
		},

		/**
		 * Custom filter for site autocomplete with normalized (accent-insensitive) search
		 * @param item The site item
		 * @param queryText The search query
		 * @returns Whether the item matches the query
		 */
		siteFilter(item, queryText) {
			if (!queryText) return true;

			const normalizedQuery = this.normalizeString(queryText);
			const normalizedName = this.normalizeString(item.name);
			const normalizedId = this.normalizeString(item.id);

			return normalizedName.includes(normalizedQuery) ||
				normalizedId.includes(normalizedQuery);
		},

		/**
		 * Handle site autocomplete selection change - blur input to prevent further typing
		 */
		onSiteAutocompleteChange() {
			// Clear search query and blur the input to prevent typing after selection
			this.siteSearchQuery = "";
			this.$nextTick(() => {
				if (this.$refs.siteAutocomplete) {
					this.$refs.siteAutocomplete.blur();
				}
			});
		},

		async fetchSites() {
			this.sitesLoading = true;

			try {
				const response = await this.axios.get("/devops/yealink/sites");

				this.sites = response.data.sites || [];
			} catch (err) {
				console.error("Failed to fetch Yealink sites:", err);
				// Don't show error to user, just log it - sites list is optional
			} finally {
				this.sitesLoading = false;
			}
		},

		async fetchDevices(options) {
			const silent = options?.silent || false;
			let success = true;

			if (!silent) {
				this.loading = true;
				this.error = null;
				this.noSiteFound = false;
			}

			try {
				// Build URL with optional siteId query parameter
				let url = "/devops/yealink/devices";

				if (this.selectedSiteId?.id) {
					url += `?siteId=${encodeURIComponent(this.selectedSiteId.id)}`;
				}

				const response = await this.axios.get(url);

				this.data = response.data;
			} catch (err) {
				console.error("Failed to fetch Yealink devices:", err);
				success = false;

				if (!silent) {
					const message = err.response?.data?.message || "";

					// Check if it's a "no site found" error
					if (err.response?.status === 404 && message.includes("No Yealink site found")) {
						this.noSiteFound = true;
					} else {
						this.error = message || this.$t("yealink-error-loading");
					}
				}
			} finally {
				if (!silent) this.loading = false;
				this.$emit("refreshed");
			}

			return { success };
		},

		async fetchDeviceModels() {
			this.deviceModelsLoading = true;

			try {
				const response = await this.axios.get("/devops/yealink/models");

				this.deviceModels = response.data;
			} catch (err) {
				console.error("Failed to fetch Yealink device models:", err);
			} finally {
				this.deviceModelsLoading = false;
			}
		},

		/**
		 * Handle site selection change
		 * @param site The selected site object or null if cleared
		 */
		onSiteSelected(site) {
			// Store the whole site object (or null if cleared)
			this.selectedSiteId = site || null;
			// Refresh devices for the selected site
			this.fetchDevices();
		},

		/**
		 * Open the site selection dialog
		 */
		openSiteSelectionDialog() {
			// Pre-select the current site if one is selected
			if (this.selectedSiteId) {
				this.siteSelectionDialog.selectedSite = this.selectedSiteId;
			} else {
				this.siteSelectionDialog.selectedSite = null;
			}
			this.siteSearchQuery = "";
			this.siteSelectionDialog.open = true;
		},

		/**
		 * Close the site selection dialog without applying changes
		 */
		closeSiteSelectionDialog() {
			this.siteSelectionDialog.open = false;
			this.siteSelectionDialog.selectedSite = null;
			this.siteSearchQuery = "";
		},

		/**
		 * Confirm the site selection and refresh data
		 */
		confirmSiteSelection() {
			// Always call onSiteSelected - if null/cleared, it will reset to default (user.clinic)
			this.onSiteSelected(this.siteSelectionDialog.selectedSite);
			this.siteSelectionDialog.open = false;
			this.siteSelectionDialog.selectedSite = null;
			this.siteSearchQuery = "";
		},

		async createSite() {
			this.creatingSite = true;

			try {
				await this.axios.post("/devops/yealink/site");
				this.$success(this.$t("yealink-create-site-success"));
				await this.fetchDevices();
			} catch (err) {
				console.error("Failed to create Yealink site:", err);
				this.$error(err.response?.data?.message || this.$t("yealink-error-create-site"));
			} finally {
				this.creatingSite = false;
			}
		},

		isDeviceOnline(device) {
			return device.deviceStatus?.toLowerCase() === "online";
		},

		isDeviceDnd(device) {
			// Check if any account on the device has DND status (status === 2)
			return device.accounts?.some(a => a.status === 2) || false;
		},

		isDevicePending(device) {
			return device.deviceStatus?.toLowerCase() === "pending";
		},

		getDeviceStatusClass(device) {
			const status = device.deviceStatus?.toLowerCase();

			if (status === "online") return "online";
			if (status === "pending") return "pending";
			return "offline";
		},

		getDeviceStatusChipColor(device) {
			const status = device.deviceStatus?.toLowerCase();

			if (status === "online") return "success";
			if (status === "pending") return "grey";
			return "error";
		},

		getFirstAccountName(device) {
			return device.accounts?.[0]?.username || "";
		},

		// Get accounts with a specific status (2=DND, 3=Not Registered, 4=Unknown)
		getAccountsWithStatus(device, status) {
			return device.accounts?.filter(a => a.status === status) || [];
		},

		formatStatus(status) {
			if (!status) return "-";
			return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
		},

		formatMac(mac) {
			if (!mac) return "-";
			// Format as XX:XX:XX:XX:XX:XX
			return mac.match(/.{1,2}/g)?.join(":").toUpperCase() || mac;
		},

		formatTimestamp(timestamp) {
			if (!timestamp) return "-";
			return new Date(timestamp).toLocaleString();
		},

		getStalenessMinutes(timestamp) {
			if (!timestamp) return Infinity;
			return moment().diff(moment(timestamp), "minutes");
		},

		getStalenessLevel(timestamp) {
			const mins = this.getStalenessMinutes(timestamp);

			if (mins < 30) return "staleness-fresh";
			if (mins < 60 * 6) return "staleness-recent";
			if (mins < 60 * 24) return "staleness-aging";
			if (mins < 60 * 24 * 7) return "staleness-stale";
			return "staleness-critical";
		},

		getStalenessColor(timestamp) {
			const mins = this.getStalenessMinutes(timestamp);

			if (mins < 30) return "#4caf50";
			if (mins < 60 * 6) return "#8bc34a";
			if (mins < 60 * 24) return "#ff9800";
			if (mins < 60 * 24 * 7) return "#f44336";
			return "#b71c1c";
		},

		getStalenessIcon(timestamp) {
			const mins = this.getStalenessMinutes(timestamp);

			if (mins < 30) return "mdi-check-circle-outline";
			if (mins < 60 * 6) return "mdi-clock-check-outline";
			if (mins < 60 * 24) return "mdi-clock-alert-outline";
			if (mins < 60 * 24 * 7) return "mdi-alert-outline";
			return "mdi-alert-circle-outline";
		},

		getStalenessText(timestamp) {
			const locale = (this.$i18n?.locale || "fr-FR").split("-")[0];

			return moment(timestamp).locale(locale).fromNow();
		},

		getAccountStatusColor(status) {
			switch (status) {
				case 1: return "success";    // Registered
				case 2: return "error";      // DND
				case 3: return "grey";       // Not registered
				case 4: return "grey";       // Unknown
				default: return "grey lighten-1";
			}
		},

		getAccountStatusIcon(status) {
			switch (status) {
				case 1: return "mdi-check-circle";        // Registered
				case 2: return "mdi-minus-circle";        // DND
				case 3: return "mdi-close-circle";        // Not registered
				case 4: return "mdi-help-circle";         // Unknown
				default: return "mdi-help-circle-outline";
			}
		},

		getAccountStatusText(status) {
			switch (status) {
				case 1: return this.$t("yealink-status-registered");
				case 2: return this.$t("yealink-status-dnd");
				case 3: return this.$t("yealink-status-not-registered");
				case 4: return this.$t("yealink-status-unknown");
				default: return this.$t("yealink-status-unknown");
			}
		},

		toggleDetails(deviceId) {
			this.$set(this.expandedDetails, deviceId, !this.expandedDetails[deviceId]);
		},

		toggleAllDevices() {
			const shouldCollapse = this.hasAnyExpanded;

			if (this.data?.devices) {
				this.data.devices.forEach(device => {
					this.$set(this.expandedDetails, device.id, !shouldCollapse);
				});
			}
		},

		async copyToClipboard(text) {
			try {
				await navigator.clipboard.writeText(text);
				this.$success(this.$t("copied-to-clipboard"));
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		/**
		 * Sanitize device input by replacing letter O with number 0
		 * and stripping colons/dashes from MAC addresses
		 */
		sanitizeDeviceInput(value) {
			if (!value) return value;
			return value.replace(/[oO]/g, "0").replace(/[:\-]/g, "");
		},

		// Reboot
		openRebootDialog(device) {
			this.rebootDialog.device = device;
			this.rebootDialog.open = true;
		},

		async rebootDevice() {
			this.rebootDialog.loading = true;
			try {
				await this.axios.post(`/devops/yealink/devices/${this.rebootDialog.device.id}/reboot`);
				this.$success(this.$t("yealink-reboot-success"));
				this.rebootDialog.open = false;
			} catch (err) {
				console.error("Failed to reboot device:", err);
				this.$error(err.response?.data?.message || this.$t("yealink-error-reboot"));
			} finally {
				this.rebootDialog.loading = false;
			}
		},

		// Factory Reset
		openFactoryResetDialog(device) {
			this.factoryResetDialog.device = device;
			this.factoryResetDialog.confirmation = "";
			this.factoryResetDialog.open = true;
		},

		closeFactoryResetDialog() {
			this.factoryResetDialog.open = false;
			this.factoryResetDialog.confirmation = "";
		},

		async factoryResetDevice() {
			this.factoryResetDialog.loading = true;
			try {
				await this.axios.post(`/devops/yealink/devices/${this.factoryResetDialog.device.id}/factory-reset`);
				this.$success(this.$t("yealink-factory-reset-success"));
				this.factoryResetDialog.open = false;
				this.factoryResetDialog.confirmation = "";
			} catch (err) {
				console.error("Failed to factory reset device:", err);
				this.$error(err.response?.data?.message || this.$t("yealink-error-factory-reset"));
			} finally {
				this.factoryResetDialog.loading = false;
			}
		},

		// Packet Capture
		openPacketCaptureDialog(device) {
			this.packetCaptureDialog.device = device;
			this.packetCaptureDialog.status = null;
			this.packetCaptureDialog.url = null;
			this.packetCaptureDialog.diagnosisId = null;
			this.packetCaptureDialog.open = true;
		},

		closePacketCaptureDialog() {
			if (this.packetCaptureDialog.pollInterval) {
				clearInterval(this.packetCaptureDialog.pollInterval);
				this.packetCaptureDialog.pollInterval = null;
			}
			this.packetCaptureDialog.open = false;
		},

		async startPacketCapture() {
			this.packetCaptureDialog.loading = true;
			this.packetCaptureDialog.status = null;
			this.packetCaptureDialog.url = null;

			try {
				const response = await this.axios.post(
					`/devops/yealink/devices/${this.packetCaptureDialog.device.id}/packet-capture/start`,
					{ networkInterface: "wan", type: 0, duration: 3600, filter: "" }
				);

				this.packetCaptureDialog.diagnosisId = response.data.diagnosisId;
				this.packetCaptureDialog.status = "inprogress";
				this.$success(this.$t("yealink-capture-started"));
				this.pollPacketCaptureStatus();
			} catch (err) {
				console.error("Failed to start packet capture:", err);
				this.$error(err.response?.data?.message || this.$t("yealink-error-packet-capture"));
			} finally {
				this.packetCaptureDialog.loading = false;
			}
		},

		async stopPacketCapture() {
			this.packetCaptureDialog.loading = true;
			try {
				await this.axios.post(
					`/devops/yealink/devices/${this.packetCaptureDialog.device.id}/packet-capture/stop`,
					{ diagnosisId: this.packetCaptureDialog.diagnosisId }
				);
				this.$success(this.$t("yealink-capture-stopped"));
				// Continue polling to get the file
			} catch (err) {
				console.error("Failed to stop packet capture:", err);
				this.$error(err.response?.data?.message || this.$t("yealink-error-packet-capture"));
			} finally {
				this.packetCaptureDialog.loading = false;
			}
		},

		pollPacketCaptureStatus() {
			if (this.packetCaptureDialog.pollInterval) {
				clearInterval(this.packetCaptureDialog.pollInterval);
			}
			this.packetCaptureDialog.pollInterval = setInterval(async () => {
				try {
					const response = await this.axios.get(
						`/devops/yealink/diagnosis/${this.packetCaptureDialog.diagnosisId}/status`
					);

					this.packetCaptureDialog.status = response.data.status;
					if (response.data.url) {
						this.packetCaptureDialog.url = response.data.url;
					}
					if (response.data.status === "success" || response.data.status === "failure") {
						clearInterval(this.packetCaptureDialog.pollInterval);
						this.packetCaptureDialog.pollInterval = null;
					}
				} catch (err) {
					console.error("Failed to get capture status:", err);
				}
			}, 3000);
		},

		getPacketCaptureStatusText() {
			switch (this.packetCaptureDialog.status) {
				case "inprogress": return this.$t("yealink-capture-in-progress");
				case "success": return this.$t("yealink-capture-complete");
				case "failure": return this.$t("yealink-capture-failed");
				default: return "";
			}
		},

		// Export Config
		async exportDeviceConfig(device) {
			this.exportConfigDialog.device = device;
			this.exportConfigDialog.status = "inprogress";
			this.exportConfigDialog.url = null;
			this.exportConfigDialog.open = true;
			this.exportConfigDialog.loading = true;

			try {
				const response = await this.axios.post(
					`/devops/yealink/devices/${device.id}/export-config`
				);

				this.exportConfigDialog.diagnosisId = response.data.diagnosisId;
				this.pollExportConfigStatus();
			} catch (err) {
				console.error("Failed to export config:", err);
				this.exportConfigDialog.status = "failure";
				this.$error(err.response?.data?.message || this.$t("yealink-error-export-config"));
			} finally {
				this.exportConfigDialog.loading = false;
			}
		},

		closeExportConfigDialog() {
			if (this.exportConfigDialog.pollInterval) {
				clearInterval(this.exportConfigDialog.pollInterval);
				this.exportConfigDialog.pollInterval = null;
			}
			this.exportConfigDialog.open = false;
		},

		pollExportConfigStatus() {
			if (this.exportConfigDialog.pollInterval) {
				clearInterval(this.exportConfigDialog.pollInterval);
			}
			this.exportConfigDialog.pollInterval = setInterval(async () => {
				try {
					const response = await this.axios.get(
						`/devops/yealink/diagnosis/${this.exportConfigDialog.diagnosisId}/status`
					);

					this.exportConfigDialog.status = response.data.status;
					if (response.data.url) {
						this.exportConfigDialog.url = response.data.url;
					}
					if (response.data.status === "success" || response.data.status === "failure") {
						clearInterval(this.exportConfigDialog.pollInterval);
						this.exportConfigDialog.pollInterval = null;
					}
				} catch (err) {
					console.error("Failed to get export status:", err);
				}
			}, 3000);
		},

		getExportStatusText() {
			switch (this.exportConfigDialog.status) {
				case "inprogress": return this.$t("yealink-export-in-progress");
				case "success": return this.$t("yealink-export-complete");
				case "failure": return this.$t("yealink-export-failed");
				default: return "";
			}
		},

		// Copy Config
		openCopyConfigDialog(device) {
			this.copyConfigDialog.device = device;
			this.copyConfigDialog.sourceDevice = null;
			this.copyConfigDialog.error = null;
			this.copyConfigDialog.open = true;
		},

		closeCopyConfigDialog() {
			this.copyConfigDialog.open = false;
			this.copyConfigDialog.sourceDevice = null;
			this.copyConfigDialog.error = null;
		},

		async copyConfig() {
			this.copyConfigDialog.loading = true;
			this.copyConfigDialog.error = null;

			try {
				await this.axios.post(
					`/devops/yealink/devices/${this.copyConfigDialog.device.id}/copy-config`,
					{ sourceDeviceId: this.copyConfigDialog.sourceDevice.id }
				);
				this.$success(this.$t("yealink-copy-config-success"));
				this.copyConfigDialog.open = false;
			} catch (err) {
				console.error("Failed to copy config:", err);
				this.copyConfigDialog.error = err.response?.data?.message || this.$t("yealink-error-copy-config");
			} finally {
				this.copyConfigDialog.loading = false;
			}
		},

		// Add Device
		openAddDeviceDialog() {
			this.addDeviceDialog.mac = "";
			this.addDeviceDialog.sn = "";
			this.addDeviceDialog.modelId = null;
			this.addDeviceDialog.name = "";
			this.addDeviceDialog.error = null;
			this.addDeviceDialog.scanLoading = false;
			this.addDeviceDialog.scanError = null;
			this.addDeviceDialog.open = true;
		},

		closeAddDeviceDialog() {
			this.addDeviceDialog.open = false;
			this.addDeviceDialog.mac = "";
			this.addDeviceDialog.sn = "";
			this.addDeviceDialog.modelId = null;
			this.addDeviceDialog.name = "";
			this.addDeviceDialog.skipRps = false;
			this.addDeviceDialog.error = null;
			this.addDeviceDialog.scanLoading = false;
			this.addDeviceDialog.scanError = null;
		},

		async addDevice() {
			if (!this.$refs.addDeviceForm.validate()) return;

			this.addDeviceDialog.loading = true;
			this.addDeviceDialog.error = null;

			try {
				const body = {
					mac: this.addDeviceDialog.mac.replace(/[:\-\s]/g, "").toUpperCase(),
					sn: this.addDeviceDialog.sn.trim(),
					modelId: this.addDeviceDialog.modelId,
					skipRps: this.addDeviceDialog.skipRps
				};

				if (this.addDeviceDialog.name?.trim()) {
					body.name = this.addDeviceDialog.name.trim();
				}

				// Build URL with optional siteId query parameter
				let url = "/devops/yealink/devices";

				if (this.selectedSiteId?.id) {
					url += `?siteId=${encodeURIComponent(this.selectedSiteId.id)}`;
				}

				await this.axios.post(url, body);
				this.$success(this.$t("yealink-add-device-success"));
				this.closeAddDeviceDialog();
			} catch (err) {
				console.error("Failed to add device:", err);
				this.addDeviceDialog.error = err.response?.data?.message || this.$t("yealink-error-add-device");
			} finally {
				this.addDeviceDialog.loading = false;
			}
		},

		// Import Devices
		openImportDevicesDialog() {
			this.importDevicesDialog.file = null;
			this.importDevicesDialog.results = null;
			this.importDevicesDialog.error = null;
			this.importDevicesDialog.open = true;
		},

		closeImportDevicesDialog() {
			this.importDevicesDialog.open = false;
			this.importDevicesDialog.file = null;
			this.importDevicesDialog.results = null;
			this.importDevicesDialog.error = null;
		},

		async importDevices() {
			if (!this.importDevicesDialog.file) return;

			this.importDevicesDialog.loading = true;
			this.importDevicesDialog.results = null;

			try {
				const formData = new FormData();

				formData.append("file", this.importDevicesDialog.file);

				let url = "/devops/yealink/devices/import";

				if (this.selectedSiteId?.id) {
					url += `?siteId=${encodeURIComponent(this.selectedSiteId.id)}`;
				}

				const response = await this.axios.post(url, formData, {
					headers: { "Content-Type": "multipart/form-data" }
				});

				this.importDevicesDialog.results = response.data;

				if (response.data.added > 0) {
					this.$success(this.$t("yealink-import-success", { count: response.data.added, total: response.data.total }));
					await this.fetchDevices();
				}
			} catch (err) {
				console.error("Failed to import devices:", err);
				this.importDevicesDialog.error = err.response?.data?.message || this.$t("yealink-error-import-devices");
				this.$error(this.importDevicesDialog.error);
			} finally {
				this.importDevicesDialog.loading = false;
			}
		},

		// Edit Device Name
		openEditNameDialog(device) {
			this.editNameDialog.device = device;
			this.editNameDialog.name = device.name || "";
			this.editNameDialog.error = null;
			this.editNameDialog.open = true;
		},

		closeEditNameDialog() {
			this.editNameDialog.open = false;
			this.editNameDialog.device = null;
			this.editNameDialog.name = "";
			this.editNameDialog.error = null;
		},

		async saveDeviceName() {
			this.editNameDialog.loading = true;
			this.editNameDialog.error = null;

			try {
				await this.axios.patch(
					`/devops/yealink/devices/${this.editNameDialog.device.id}`,
					{ name: this.editNameDialog.name.trim() }
				);
				this.$success(this.$t("yealink-edit-name-success"));
				this.closeEditNameDialog();
			} catch (err) {
				console.error("Failed to update device name:", err);
				this.editNameDialog.error = err.response?.data?.message || this.$t("yealink-error-edit-name");
			} finally {
				this.editNameDialog.loading = false;
			}
		},

		// Label Photo Scanner
		async onLabelPhotoSelected(event) {
			let file = event.target.files?.[0];

			if (!file) return;

			console.log("[scan] Original file:", file.name, "type:", file.type, "size:", file.size);

			this.addDeviceDialog.scanLoading = true;
			this.addDeviceDialog.scanError = null;

			try {
				// Convert HEIC/HEIF to JPEG if needed
				const isHeic = file.type === "image/heic" || file.type === "image/heif"
					|| /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name);

				if (isHeic) {
					console.log("[scan] HEIC detected, converting...");
					const { heicTo } = await import("heic-to");
					const jpegBlob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.9 });

					file = new File([jpegBlob], file.name.replace(/\.heic$/i, ".jpg"), { type: "image/jpeg" });
					console.log("[scan] HEIC converted, new size:", file.size);
				}

				const results = [];

				// Use native BarcodeDetector if available, otherwise use polyfill
				let DetectorClass = (typeof BarcodeDetector !== "undefined") ? BarcodeDetector : null;

				if (!DetectorClass) {
					console.log("[scan] Native BarcodeDetector not available, loading polyfill...");
					const { prepareZXingModule, ZXING_WASM_VERSION } = await import("barcode-detector/ponyfill");

					// Override the locateFile function
					prepareZXingModule({
						overrides: {
							locateFile: (path, prefix) => {
								if (path.endsWith(".wasm")) {
									return `https://unpkg.com/zxing-wasm@${ZXING_WASM_VERSION}/dist/reader/${path}`;
								}
								return prefix + path;
							}
						}
					});

					const polyfill = await import("barcode-detector/ponyfill");

					DetectorClass = polyfill.BarcodeDetector;
					console.log("[scan] Polyfill loaded:", !!DetectorClass);
				} else {
					console.log("[scan] Using native BarcodeDetector");
				}

				try {
					const bitmap = await createImageBitmap(file);

					console.log("[scan] ImageBitmap created:", bitmap.width, "x", bitmap.height);
					const detector = new DetectorClass({ formats: ["code_128"]});
					const detectedBarcodes = await detector.detect(bitmap);

					console.log("[scan] BarcodeDetector found:", detectedBarcodes.length, "barcodes");

					for (const bc of detectedBarcodes) {
						console.log("[scan] Barcode raw:", bc.rawValue, "format:", bc.format);
						const sanitized = this.sanitizeDeviceInput(bc.rawValue);

						if (sanitized && !results.includes(sanitized)) {
							results.push(sanitized);
						}
					}
					bitmap.close();
				} catch (detErr) {
					console.error("[scan] BarcodeDetector failed:", detErr);
				}

				console.log("[scan] Total results:", results);

				if (results.length === 0) {
					this.addDeviceDialog.mac = "";
					this.addDeviceDialog.sn = "";
					this.addDeviceDialog.scanError = this.$t("yealink-scan-no-barcodes");
					return;
				}

				// Classify: exactly 12 hex chars = MAC, anything else = serial
				let mac = "";
				let sn = "";

				for (const val of results) {
					if (val.length === 12 && /^[0-9A-Fa-f]{12}$/.test(val)) {
						mac = val;
					} else {
						sn = val;
					}
				}

				this.addDeviceDialog.mac = mac;
				this.addDeviceDialog.sn = sn;

				if (mac || sn) {
					this.$success(this.$t("yealink-scan-success"));
				} else {
					this.addDeviceDialog.scanError = this.$t("yealink-scan-no-barcodes");
				}
			} catch (err) {
				console.error("Failed to scan label photo:", err);
				this.addDeviceDialog.scanError = this.$t("yealink-scan-error");
			} finally {
				this.addDeviceDialog.scanLoading = false;

				// Reset the input so the same file can be re-selected (must be after processing)
				if (this.$refs.labelPhotoInput) {
					this.$refs.labelPhotoInput.value = "";
				}
			}
		}
	}
};
</script>

<style scoped>
/* Embedded mode: make card and card-text participate in flex layout */
.v-card {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

.yealink-panel-main {
	border-radius: 0 !important;
}

.yealink-panel-main > .v-card__text {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow: hidden;
	padding: 0 !important;
}

.yealink-content {
	display: flex;
	flex-direction: column;
	gap: 8px;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

/* Change Site Button */
.change-site-btn {
	opacity: 0.5;
	transition: opacity 0.15s ease;
}

.change-site-btn:hover {
	opacity: 1;
}

/* Mobile xs breakpoint - icon only, square button */
@media (max-width: 599px) {
	.device-name-row .status-chip {
		display: none;
	}

	.device-header {
		flex-wrap: wrap;
	}

	/* Account chips: full width with text on mobile */
	.header-accounts {
		max-width: none;
		flex-wrap: wrap;
	}

	.device-net {
		order: 9;
		width: 100%;
		margin-top: 2px;
	}

	.account-chip {
		max-width: none;
	}

	.device-actions {
		order: 10;
		width: 100%;
		justify-content: space-evenly;
		margin-top: 4px;
		padding: 4px 2px;
		background: #fafafa;
		border-top: 1px solid #f0f0f0;
		border-radius: 0 0 8px 8px;
	}

	.action-icon {
		width: 32px !important;
		height: 32px !important;
		opacity: 0.7;
	}

	.action-separator {
		height: 20px;
	}
}

/* Site Selection Autocomplete */
.site-selection-text {
	max-width: 100%;
	display: block;
}

/* Hide the text input when a site is selected */
.v-autocomplete.v-select--is-menu-active .v-select__selections input,
.v-autocomplete:not(.v-select--is-menu-active) .v-select__selections input[aria-expanded="false"] {
	max-width: 0;
	min-width: 0;
	width: 0;
	padding: 0;
}

/* Site Panel */
.site-panel {
	background: white;
	border-bottom: 1px solid #e8eaed;
	overflow: hidden;
	flex-shrink: 0;
}

.site-panel-header {
	padding: 12px 16px;
}

.site-panel-left {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.site-panel-info {
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
}

.site-panel-name-row {
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
}

.site-panel-name {
	font-size: 14px;
	font-weight: 600;
	color: #2c3e50;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.3;
}

.site-panel-id {
	font-size: 11px;
	color: #8a94a6;
	font-family: 'SF Mono', 'Fira Code', monospace;
	white-space: nowrap;
	line-height: 1.3;
}

/* Change Site Button */
.change-site-btn {
	opacity: 0.5;
	transition: opacity 0.15s ease;
}

.change-site-btn:hover {
	opacity: 1;
}

/* Status Metrics - inline badges */
.site-panel-metrics {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
}

.metric-badge {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	color: #5a6577;
	line-height: 1;
}

.metric-badge strong {
	font-weight: 600;
	color: #2c3e50;
}

.metric-sep {
	color: #c4cad3;
	font-size: 14px;
	line-height: 1;
}

.metric-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	flex-shrink: 0;
}

.metric-online .metric-dot {
	background: #66bb6a;
}

.metric-offline .metric-dot {
	background: #ef5350;
}

.metric-pending .metric-dot {
	background: #bdbdbd;
}

.metric-registered .metric-dot {
	background: #42a5f5;
}

.metric-unregistered .metric-dot {
	background: #cfd8dc;
}

.metric-dnd .metric-dot {
	background: #ffa726;
}

@media (max-width: 600px) {
	.site-panel-header {
		padding: 10px 12px;
	}

	.site-panel-name {
		font-size: 12px;
	}

	.site-panel-id {
		font-size: 9px;
	}

	.site-panel-metrics {
		gap: 4px;
	}

	.metric-badge {
		font-size: 11px;
	}
}

/* Devices Section - Just the list */
.devices-section {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

/* Devices Toolbar - Part of Site Panel */
.devices-toolbar {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 16px 12px;
}

.devices-toolbar .search-field {
	flex: 1;
	min-width: 0;
}

.devices-toolbar .search-field >>> .v-input__slot {
	min-height: 32px !important;
	border-radius: 6px !important;
	padding: 0 8px !important;
	background: #f5f7fa !important;
	border: 1px solid #e0e3e8 !important;
}

.devices-toolbar .search-field >>> fieldset {
	border: none !important;
}

.devices-toolbar .search-field >>> .v-input__slot:hover {
	border-color: #c4cad3 !important;
}

.devices-toolbar .search-field >>> .v-input__prepend-inner {
	margin-top: 2px !important;
	margin-right: 4px !important;
	align-self: center !important;
}

.devices-toolbar .search-field >>> .v-input__prepend-inner .v-icon {
	color: #8a94a6 !important;
	font-size: 18px !important;
}

.devices-toolbar .search-field >>> input {
	font-size: 13px !important;
	color: #2c3e50 !important;
}

.devices-toolbar .search-field >>> input::placeholder {
	color: #a0a8b4 !important;
}

.devices-toolbar .action-icon-btn {
	flex-shrink: 0;
	min-width: 32px !important;
	min-height: 32px !important;
	padding: 0 !important;
	border-radius: 6px !important;
	transition: all 0.15s ease !important;
	background: #f5f7fa !important;
	border: 1px solid #e0e3e8 !important;
	display: flex;
	align-items: center;
	justify-content: center;
}

.devices-toolbar .expand-icon-btn {
	background: #f5f7fa !important;
	border: 1px solid #e0e3e8 !important;
}

.devices-toolbar .expand-icon-btn .v-icon {
	color: #5a6577 !important;
}

.devices-toolbar .expand-icon-btn:hover {
	background: #eceef2 !important;
	border-color: #c4cad3 !important;
	box-shadow: none;
	transform: none;
}

.devices-toolbar .add-icon-btn {
	background: #f5f7fa !important;
	border: 1px solid #e0e3e8 !important;
}

.devices-toolbar .add-icon-btn .v-icon {
	color: #1e88e5 !important;
}

.devices-toolbar .add-icon-btn:hover {
	background: #e8f0fe !important;
	border-color: #90caf9 !important;
	box-shadow: none;
	transform: none;
}

.devices-toolbar .import-icon-btn {
	background: #f5f7fa !important;
	border: 1px solid #e0e3e8 !important;
}

.devices-toolbar .import-icon-btn .v-icon {
	color: #f57c00 !important;
}

.devices-toolbar .import-icon-btn:hover {
	background: #fff3e0 !important;
	border-color: #ffb74d !important;
	box-shadow: none;
	transform: none;
}

.devices-toolbar .expand-all-btn {
	text-transform: none !important;
	font-weight: 500 !important;
	letter-spacing: 0;
	font-size: 12px;
	color: #5a6577 !important;
	border: 1px solid #e0e3e8 !important;
	background: #f5f7fa !important;
	border-radius: 6px !important;
	padding: 2px 10px !important;
}

.devices-toolbar .expand-all-btn:hover {
	background: #eceef2 !important;
	border-color: #c4cad3 !important;
}

@media (max-width: 600px) {
	.expand-all-btn.hidden-xs-only {
		display: none !important;
	}

	.add-device-btn.hidden-xs-only {
		display: none !important;
	}

	.import-devices-btn.hidden-xs-only {
		display: none !important;
	}
}

@media (max-width: 500px) {
	.devices-toolbar {
		padding: 6px 12px 8px;
		gap: 4px;
	}

	.devices-toolbar .search-field {
		flex: 1 1 auto;
		min-width: 80px;
		order: 1;
	}

	.devices-toolbar .search-field >>> .v-input__slot {
		min-height: 30px !important;
		padding: 0 6px !important;
	}

	.devices-toolbar .action-icon-btn {
		min-width: 30px !important;
		min-height: 30px !important;
	}

	.devices-toolbar .expand-icon-btn {
		order: 2;
	}

	.devices-toolbar .add-icon-btn {
		order: 3;
	}

	.devices-toolbar .import-icon-btn {
		order: 4;
	}
}

.devices-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding-right: 4px;
}

/* Custom scrollbar */
.devices-list::-webkit-scrollbar {
	width: 6px;
}

.devices-list::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 3px;
}

.devices-list::-webkit-scrollbar-thumb {
	background: #bdbdbd;
	border-radius: 3px;
}

.devices-list::-webkit-scrollbar-thumb:hover {
	background: #9e9e9e;
}

/* Device Card */
.device-card {
	background: #fafafa;
	border-radius: 8px;
	border-left: 4px solid #bdbdbd;
	border-top: 1px solid #d0d0d0;
	border-bottom: 1px solid #d0d0d0;
	border-right: 1px solid #d0d0d0;
	margin-left: 8px;
	margin-right: 8px;
	margin-bottom: 8px;
	transition: all 0.2s ease;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.device-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.device-card.device-online {
	border-left-color: #4caf50;
}

.device-card.device-offline {
	border-left-color: #f44336;
}

.device-card.device-pending {
	border-left-color: #9e9e9e;
}

.device-card.expanded {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.device-header {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 10px;
	background: white;
	border-radius: 8px;
	cursor: pointer;
	user-select: none;
}

.device-card.expanded .device-header {
	border-bottom: 1px solid #f0f0f0;
	border-radius: 8px 8px 0 0;
}

.device-status-indicator {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;
}

.device-status-indicator.online {
	background: #4caf50;
	box-shadow: 0 0 6px rgba(76, 175, 80, 0.5);
}

.device-status-indicator.offline {
	background: #f44336;
	box-shadow: 0 0 6px rgba(244, 67, 54, 0.5);
}

.device-status-indicator.pending {
	background: #9e9e9e;
	box-shadow: 0 0 6px rgba(158, 158, 158, 0.5);
}

.device-title {
	flex: 1;
	min-width: 0;
}

.device-name-row {
	display: flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
}

.device-name {
	font-weight: 600;
	font-size: 13px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.edit-name-btn {
	opacity: 0.5;
	transition: opacity 0.2s;
	flex-shrink: 0;
}

.edit-name-btn:hover {
	opacity: 1;
}

.device-model {
	font-size: 11px;
	color: #757575;
	display: block;
}

.status-chip {
	flex-shrink: 0;
}

/* Header Account Chips */
.header-accounts {
	display: flex;
	align-items: center;
	gap: 3px;
	flex-shrink: 0;
	max-width: 240px;
	overflow: hidden;
}

/* Live transport / STUN chips */
.device-net {
	display: flex;
	align-items: center;
	gap: 3px;
	flex-shrink: 0;
}

.net-chip {
	--chip-color: #9ca3af;
	display: inline-flex;
	align-items: center;
	gap: 3px;
	height: 18px;
	padding: 0 7px;
	border-radius: 9px;
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.3px;
	white-space: nowrap;
	color: var(--chip-color);
	background: color-mix(in srgb, var(--chip-color) 12%, #fff);
	border: 1px solid color-mix(in srgb, var(--chip-color) 40%, #fff);
}

.net-chip .v-icon {
	font-size: 11px !important;
	color: var(--chip-color) !important;
}

.net-chip-loading {
	--chip-color: #00acc1;
	font-weight: 500;
	border-style: dashed;
}

.net-chip-stun.stun-on {
	--chip-color: #16a34a;
}

.net-chip-stun.stun-off {
	--chip-color: #64748b;
}

.net-chip-stun.stun-default {
	--chip-color: #9ca3af;
	font-weight: 500;
	border-style: dashed;
}

.net-chip-stun.stun-mixed {
	--chip-color: #dc2626;
}

.net-chip-unknown.state-offline {
	--chip-color: #9ca3af;
	font-weight: 500;
}

.net-chip-unknown.state-error,
.net-chip-unknown.state-timeout {
	--chip-color: #dc2626;
}

/* Network Section (expanded) */
.network-section {
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px dashed #e0e0e0;
}

.network-scanned-at {
	margin-left: auto;
	font-size: 10px;
	color: #9e9e9e;
}

.network-grid {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 6px;
}

.network-account {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px 8px;
	background: white;
	border: 1px solid #e8e8e8;
	border-radius: 6px;
	padding: 5px 10px;
	font-size: 10.5px;
}

.network-account .line-number {
	min-width: 110px;
}

.network-global {
	background: #f8fafb;
}

.network-warn {
	display: inline-flex;
	align-items: center;
	color: #92400e;
	font-weight: 600;
	margin-left: auto;
}

.account-chip {
	max-width: 120px;
	height: 18px !important;
	padding: 0 6px !important;
	font-size: 10px;
}

.account-chip .v-icon {
	font-size: 11px !important;
	margin-right: 2px;
	margin-left: -2px;
}

.account-chip-text {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* Device Action Icons */
.device-actions {
	display: flex;
	align-items: center;
	gap: 2px;
	flex-shrink: 0;
	padding: 2px 4px;
	border-radius: 8px;
	background: #f5f5f5;
	transition: background 0.2s;
}

.device-header:hover .device-actions {
	background: #eeeeee;
}

.action-icon {
	width: 26px !important;
	height: 26px !important;
	transition: all 0.15s ease;
	border-radius: 6px !important;
	opacity: 0.55;
}

.action-icon:hover {
	opacity: 1;
	transform: scale(1.15);
}

.action-configure:hover {
	background: rgba(76, 175, 80, 0.12) !important;
}
.action-configure:hover .v-icon { color: #4caf50 !important; }

.action-reboot:hover {
	background: rgba(255, 152, 0, 0.12) !important;
}
.action-reboot:hover .v-icon { color: #ff9800 !important; }

.action-reset:hover {
	background: rgba(244, 67, 54, 0.12) !important;
}
.action-reset:hover .v-icon { color: #f44336 !important; }

.action-capture:hover {
	background: rgba(156, 39, 176, 0.12) !important;
}
.action-capture:hover .v-icon { color: #9c27b0 !important; }

.action-export:hover {
	background: rgba(33, 150, 243, 0.12) !important;
}
.action-export:hover .v-icon { color: #2196f3 !important; }

.action-copy:hover {
	background: rgba(0, 150, 136, 0.12) !important;
}
.action-copy:hover .v-icon { color: #009688 !important; }

.action-separator {
	width: 1px;
	height: 16px;
	background: #e0e0e0;
	margin: 0 2px;
	flex-shrink: 0;
}

/* Staleness Badge */
.staleness-badge {
	display: inline-flex;
	align-items: center;
	gap: 3px;
	padding: 1px 6px;
	border-radius: 8px;
	white-space: nowrap;
	line-height: 1;
	margin-top: 1px;
	margin-left: -8px;
	font-size: 10px;
}

.staleness-badge .v-icon {
	flex-shrink: 0;
}

.staleness-text {
	font-size: 10px;
	font-weight: 500;
}

.staleness-fresh {
	background: rgba(76, 175, 80, 0.08);
	color: #4caf50;
}

.staleness-recent {
	background: rgba(139, 195, 74, 0.08);
	color: #689f38;
}

.staleness-aging {
	background: rgba(255, 152, 0, 0.1);
	color: #f57c00;
}

.staleness-stale {
	background: rgba(244, 67, 54, 0.08);
	color: #e53935;
}

.staleness-critical {
	background: rgba(183, 28, 28, 0.1);
	color: #b71c1c;
}

@media (max-width: 599px) {
	.staleness-badge {
		padding: 2px 5px;
	}

	.staleness-text {
		font-size: 9px;
	}
}

.expand-btn {
	opacity: 0.5;
	transition: opacity 0.2s;
	flex-shrink: 0;
}

.device-header:hover .expand-btn {
	opacity: 1;
}

.device-body {
	padding: 10px 12px;
	animation: slideDown 0.15s ease-out;
}

@keyframes slideDown {
	from {
		opacity: 0;
		transform: translateY(-8px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Info Grid */
.info-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
}

.info-item {
	display: flex;
	align-items: center;
	font-size: 11px;
}

.info-label {
	color: #757575;
	margin-right: 4px;
}

.info-value {
	color: #424242;
}

.info-value.monospace {
	font-family: "Monaco", "Menlo", monospace;
	font-size: 10px;
	background: #f5f5f5;
	padding: 2px 4px;
	border-radius: 3px;
}

.ip-link {
	color: #1976d2;
	text-decoration: none;
}

.ip-link:hover {
	text-decoration: underline;
}

.copy-btn {
	opacity: 0;
	transition: opacity 0.2s;
}

.info-item:hover .copy-btn {
	opacity: 1;
}

/* Accounts Section */
.accounts-section {
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px dashed #e0e0e0;
}

.accounts-header {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	padding: 4px 0;
	font-size: 12px;
	color: #616161;
}

.account-pill {
	max-width: 150px;
}

.account-pill .v-chip__content {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.accounts-title {
	font-weight: 500;
}

.accounts-list {
	margin-top: 8px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.account-item {
	background: white;
	border-radius: 6px;
	padding: 8px 10px;
	border: 1px solid #e8e8e8;
}

.account-line {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 6px;
}

.line-number {
	font-weight: 600;
	font-size: 11px;
	color: #424242;
}

.account-details {
	display: flex;
	flex-wrap: wrap;
	gap: 4px 12px;
}

.account-detail {
	font-size: 10px;
}

.detail-label {
	color: #9e9e9e;
	margin-right: 2px;
}

.detail-value {
	color: #616161;
}

.detail-value.monospace {
	font-family: "Monaco", "Menlo", monospace;
	background: #f5f5f5;
	padding: 1px 4px;
	border-radius: 2px;
}

/* Empty State */
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	gap: 12px;
}

.empty-text {
	color: #9e9e9e;
	font-size: 14px;
}

/* No Site State */
.no-site-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	gap: 12px;
}

.no-site-text {
	color: #9e9e9e;
	font-size: 14px;
	text-align: center;
}

/* Error Alert */
.error-alert {
	word-break: break-word;
	overflow-wrap: break-word;
}

/* Scan Label Button */
.scan-label-wrapper {
	text-align: center;
}

.scan-label-btn {
	border: 2px dashed rgba(0, 172, 193, 0.5) !important;
	border-radius: 12px !important;
	padding: 14px 16px !important;
	min-height: 120px !important;
	text-transform: none !important;
	font-weight: 500;
	letter-spacing: 0;
	color: #00acc1 !important;
	background: rgba(0, 172, 193, 0.04) !important;
	transition: all 0.2s ease;
	display: flex !important;
	flex-direction: column !important;
	align-items: center !important;
	justify-content: center !important;
}

.scan-label-btn:hover {
	border-color: rgba(0, 172, 193, 0.8) !important;
	background: rgba(0, 172, 193, 0.1) !important;
}

.scan-label-btn .v-btn__content {
	flex-direction: column !important;
	gap: 8px;
}

.scan-label-btn .v-icon {
	color: #00acc1;
	font-size: 32px !important;
	margin-right: 0 !important;
}

#label-scanner-tmp {
	display: none !important;
}

.copy-icon {
	cursor: pointer;
	opacity: 0.5;
	transition: opacity 0.2s ease;
}

.copy-icon:hover {
	opacity: 1;
}

.rps-hint {
	display: flex;
	align-items: center;
	font-size: 12px;
	color: #78909c;
	background: #f5f8fa;
	border-radius: 6px;
	padding: 8px 12px;
}

.rps-link {
	color: #1976d2;
	font-weight: 600;
	text-decoration: none;
}

.rps-link:hover {
	text-decoration: underline;
}

.add-device-error {
	word-break: break-word;
	overflow-wrap: break-word;
}

.copy-field {
	position: relative;
}

.copy-field >>> .v-input__append-outer {
	position: absolute !important;
	right: 12px;
	top: -25px;
	bottom: 0;
	margin: 0 !important;
	display: flex !important;
	align-items: center !important;
	z-index: 1;
}

/* Clear button in search field - white color */
.devices-toolbar .search-field >>> .v-input__append-inner .v-icon {
	color: white !important;
}
</style>
